import { DEPOSIT_RAMP, PAYOUT_RAMP, RAMP_INK } from './seriesColors'
import { ShortfallReason, ShortfallTimeseriesBucket } from './types'

export type Side = 'deposit' | 'payout'

/** Одна корзина: имя, цвет каждой стороны и обе меры по часам. */
export type BasketCells = {
  basket: string
  deposit: SideCells
  payout: SideCells
}

export type SideCells = {
  color: string
  counts: number[]
  amounts: number[]
  /** Разбивка по причинам, по часам. */
  reasons: ShortfallReason[][]
}

/**
 * Ряд heatmap: имя (корзина) и её индекс в `cells`. Ряды идут снизу вверх, как
 * их рисует Apex, поэтому индекс не совпадает с позицией ряда.
 */
export type HeatRow = {
  name: string
  basketIndex: number
}

/** Чем закрасить и подписать одну половину ячейки. */
export type HalfPaint = {
  color: string
  ink: string
  label: string
}

/** Обе половины ячейки: верх — депозиты, низ — выплаты. null = не рисуем. */
export type CellPaint = {
  top: HalfPaint | null
  bottom: HalfPaint | null
}

// Рампы живут в общем модуле палитры: правило «депозиты синие, выплаты зелёные»
// держится на весь дашборд, а не на один блок. Ступень внутри семейства =
// насыщенность события: у кубиков по количеству, у колонок по корзине.
export { DEPOSIT_RAMP, PAYOUT_RAMP, RAMP_INK }

export const SIDE_LABEL: Record<Side, string> = {
  deposit: 'Депозиты без выплаты',
  payout: 'Выплаты без депозита',
}

/** Единица счёта у серий разная: депозит — «деп», выплата — «выпл». */
export const SIDE_UNIT: Record<Side, string> = { deposit: 'деп', payout: 'выпл' }

/** Ступеней насыщенности столько же, сколько цветов в рампе кубиков. */
export const HEAT_STEPS = DEPOSIT_RAMP.length

/**
 * Плоская таблица «корзина × сторона × час» — общий источник для обоих видов
 * графика; виды отличаются только порядком рядов и тем, какую меру рисуют.
 *
 * Корзина «Остаток 0» (0–1) отбрасывается: это полоса остатка выплаты, событие в
 * неё попасть не может, и она давала бы всегда пустые ряды в обоих видах.
 */
export function buildBasketCells(buckets: ShortfallTimeseriesBucket[]): BasketCells[] {
  const baskets = buckets[0]?.baskets ?? []

  return baskets
    .map((b, index) => ({ b, index }))
    .filter(({ b }) => b.max > 1)
    .map(({ b, index }, n) => ({
      basket: b.name,
      deposit: {
        color: DEPOSIT_RAMP[n % DEPOSIT_RAMP.length],
        counts: buckets.map((bu) => bu.baskets[index]?.deposit_shortfall_count ?? 0),
        amounts: buckets.map((bu) => bu.baskets[index]?.deposit_shortfall_amount ?? 0),
        reasons: buckets.map((bu) => bu.baskets[index]?.deposit_reasons ?? []),
      },
      payout: {
        color: PAYOUT_RAMP[n % PAYOUT_RAMP.length],
        counts: buckets.map((bu) => bu.baskets[index]?.payout_shortfall_count ?? 0),
        amounts: buckets.map((bu) => bu.baskets[index]?.payout_shortfall_amount ?? 0),
        reasons: buckets.map((bu) => bu.baskets[index]?.payout_reasons ?? []),
      },
    }))
}

/**
 * Ряды кубиков: по одному на корзину, **сверху вниз от крупных сумм к мелким**
 * («От 10к» первой строкой, «До 5к» последней) — так же, как читаются корзины в
 * остальных блоках дашборда. Apex рисует series[0] верхней строкой, поэтому
 * порядок корзин разворачивается.
 */
export function buildHeatRows(cells: BasketCells[]): HeatRow[] {
  return cells
    .map((c, basketIndex) => ({ name: c.basket, basketIndex }))
    .reverse()
}

/**
 * Серии кубиков: значение ячейки — количество депозитов без выплаты. Само по
 * себе оно цвет уже не задаёт (ячейку Apex красит нейтралью темы) — значение
 * нужно ему для сетки, ховера и тултипа. Обе половины рисует `core/heatSplit.ts`.
 */
export function buildHeatSeries(cells: BasketCells[]): {
  series: Array<{ name: string; data: number[] }>
  rows: HeatRow[]
} {
  const rows = buildHeatRows(cells)

  return {
    series: rows.map((row) => ({ name: row.name, data: cells[row.basketIndex].deposit.counts })),
    rows,
  }
}

/**
 * Обе половины ячейки: верх — депозиты, низ — выплаты. Шкала насыщенности одна
 * на обе стороны (`max` — по обеим сериям), иначе «одна половина горит, другая
 * нет» перестало бы значить разницу в количестве.
 *
 * Пустая сторона закрашивается нейтралью темы, а не пропускается: ячейка обязана
 * читаться поделённой всегда, иначе час с одними депозитами выглядит сплошным
 * блоком рядом с поделёнными соседями. Полностью пустая ячейка не рисуется вовсе
 * — её рисует сам Apex.
 */
export function cellPaint(
  cells: BasketCells[],
  rows: HeatRow[],
  max: number,
  emptyColor: string,
  seriesIndex: number,
  hour: number
): CellPaint {
  const empty: CellPaint = { top: null, bottom: null }
  const row = rows[seriesIndex]
  if (!row) return empty

  const basket = cells[row.basketIndex]
  if (!basket) return empty

  const deposits = basket.deposit.counts[hour] ?? 0
  const payouts = basket.payout.counts[hour] ?? 0
  if (deposits === 0 && payouts === 0) return empty

  const side = (count: number, ramp: string[]): HalfPaint => {
    const step = intensityIndex(count, max, HEAT_STEPS)
    return step === 0
      ? { color: emptyColor, ink: emptyColor, label: '' }
      : { color: ramp[step - 1], ink: RAMP_INK[step - 1], label: String(count) }
  }

  return { top: side(deposits, DEPOSIT_RAMP), bottom: side(payouts, PAYOUT_RAMP) }
}

/**
 * Серии колонок: прежний стек по корзинам, только теперь **две группы на час** —
 * депозиты и выплаты рядом (`series[].group`, появился в Apex 3.42). Высота — в
 * деньгах. Цвета возвращаются тем же порядком, чтобы `options.colors` и серии не
 * разъехались.
 */
export function buildColumnSeries(cells: BasketCells[]): {
  series: Array<{ name: string; group: string; data: number[] }>
  colors: string[]
} {
  return {
    series: [
      ...cells.map((c) => ({
        name: `${c.basket} · депозиты`,
        group: 'deposits',
        data: c.deposit.amounts,
      })),
      ...cells.map((c) => ({
        name: `${c.basket} · выплаты`,
        group: 'payouts',
        data: c.payout.amounts,
      })),
    ],
    colors: [...cells.map((c) => c.deposit.color), ...cells.map((c) => c.payout.color)],
  }
}

/** Итог часа по одной стороне — по всем корзинам, а не по наведённой ячейке. */
export function hourTotal(cells: BasketCells[], side: Side, pointIndex: number) {
  return {
    count: cells.reduce((s, c) => s + (c[side].counts[pointIndex] ?? 0), 0),
    amount: cells.reduce((s, c) => s + (c[side].amounts[pointIndex] ?? 0), 0),
  }
}

/** Сколько событий вообще есть в срезе — обе стороны вместе. */
export function totalEvents(cells: BasketCells[]): number {
  return cells.reduce(
    (sum, c) =>
      sum +
      c.deposit.counts.reduce((s, v) => s + v, 0) +
      c.payout.counts.reduce((s, v) => s + v, 0),
    0
  )
}

/** Максимум для цветовой шкалы кубиков — по обеим сериям (шкала общая). */
export function maxCount(cells: BasketCells[]): number {
  return Math.max(0, ...cells.flatMap((c) => [...c.deposit.counts, ...c.payout.counts]))
}

/**
 * Ступень рампы для половинки ячейки: 0 — пусто, дальше 1..steps по величине
 * относительно общего максимума. Шкала одна на обе серии — иначе «одна половина
 * горит, вторая нет» перестало бы значить разницу в количестве.
 */
export function intensityIndex(count: number, max: number, steps: number): number {
  if (count <= 0 || max <= 0 || steps <= 0) return 0
  const step = Math.ceil((count / max) * steps)
  return Math.min(steps, Math.max(1, step))
}
